import { startCase } from 'lodash';
import { Link } from 'react-router-dom';

import { FrequentPassageRequest } from '@legacy-types';

import { useEvent } from '@legacy-providers/event';

import { Data } from '@shared/api/types';
import { getCrewMemberOptions } from '@shared/crew-members/endpoints/getCrewMemberOptions';
import { FrequentPassageRequestRowActions } from '@shared/frequent-passage-requests/components/FrequentPassageRequestRowActions';
import { FrequentPassageRequestStatusBadge } from '@shared/frequent-passage-requests/components/FrequentPassageRequestStatusBadge';
import { deleteFrequentPassageRequest } from '@shared/frequent-passage-requests/endpoints/deleteFrequentPassageRequest';
import { frequentPassageRequestStatusOptions } from '@shared/frequent-passage-requests/enums/FrequentPassageRequestStatus';
import { frequentPassageRequestVehicleTypeOptions } from '@shared/frequent-passage-requests/enums/FrequentPassageRequestVehicleType';
import { getFrequentPassageStickerTypeOptionsForEvent } from '@shared/frequent-passage-sticker-types/endpoints/getFrequentPassageStickerTypeOptionsForEvent';
import { getRegistrationOptionsForEvent } from '@shared/registrations/endpoints/getRegistrationOptionsForEvent';
import { DataTable } from '@shared/ui/components/DataTable';
import { LicensePlate } from '@shared/ui/components/LicensePlate';
import { OptionsQuery } from '@shared/ui/components/Select';

type Column =
  | 'registration'
  | 'stickerType'
  | 'status'
  | 'stickerCode'
  | 'licensePlate'
  | 'vehicleType'
  | 'redeemedAt'
  | 'createdAt'
  | 'actions';

type FrequentPassageRequestDataTableProps = {
  frequentPassageRequests: Data<FrequentPassageRequest>;
  columns?: Partial<{ [k in Column]: boolean }>;
  avoidPagination?: boolean;
  withoutFilters?: boolean;
};

export function FrequentPassageRequestDataTable({
  frequentPassageRequests,
  columns = {},
  avoidPagination = false,
  withoutFilters = false,
}: FrequentPassageRequestDataTableProps) {
  const event = useEvent();

  return (
    <DataTable<FrequentPassageRequest>
      data={frequentPassageRequests}
      columns={[
        {
          visible: columns.registration !== false,
          name: 'registrationId',
          label: 'Requester',
          render: (row) => (
            <>
              <Link to={`/admin/registrations/${row.registrationId}/edit`} className="font-medium">
                {row.registration.company?.name}
              </Link>
              <span className="block text-sm">{row.crewMember?.user?.fullName}</span>
            </>
          ),
        },
        {
          visible: columns.stickerType !== false,
          name: 'frequentPassageStickerTypeId',
          label: 'Sticker Type',
          render: (row) => row.frequentPassageStickerType?.name,
          filterable: {
            type: 'select',
            icon: 'tag',
            options: (query) => getFrequentPassageStickerTypeOptionsForEvent(event.id, query),
          },
        },
        {
          visible: columns.status !== false,
          name: 'status',
          render: (row) => <FrequentPassageRequestStatusBadge frequentPassageRequest={row} />,
          filterable: {
            type: 'select',
            icon: 'check',
            options: frequentPassageRequestStatusOptions,
          },
        },
        {
          visible: columns.stickerCode !== false,
          name: 'frequentPassageSticker.code',
          label: 'Code',
          type: 'number',
          filterable: {
            name: 'sticker_code',
            icon: 'barcode',
          },
          render: (row) => row.frequentPassageSticker?.code,
        },
        {
          visible: columns.licensePlate !== false,
          name: 'licensePlate',
          filterable: true,
          render: (row) => <LicensePlate>{row.licensePlate}</LicensePlate>,
        },
        {
          visible: columns.vehicleType !== false,
          label: 'Vehicle Type',
          name: 'vehicleType',
          filterable: {
            type: 'select',
            icon: 'car',
            options: frequentPassageRequestVehicleTypeOptions,
          },
        },
        {
          visible: columns.redeemedAt !== false,
          name: 'redeemedAt',
          label: 'Redeemed',
          type: 'datetime',
        },
        {
          visible: columns.actions !== false,
          render: (row) => (
            <FrequentPassageRequestRowActions
              frequentPassageRequest={row}
              onActionComplete={frequentPassageRequests.refresh}
            />
          ),
          sortable: false,
          type: 'actions',
        },
      ]}
      filters={[
        {
          visible: columns.registration !== false,
          name: 'crew_member_id',
          label: 'Crew Member',
          type: 'select',
          icon: 'user',
          options: getCrewMemberOptions,
        },
        {
          visible: columns.registration !== false,
          name: 'registration_id',
          label: 'Registration',
          type: 'select',
          icon: 'company',
          options: (options) => getRegistrationOptionsForEvent(event.id, options),
        },
      ]}
      onDelete={deleteFrequentPassageRequest}
      avoidPagination={avoidPagination}
      withoutFilters={withoutFilters}
    />
  );
}
